import { Platform } from 'react-native';
import { api } from './api';

// Helper function to convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export interface PushNotificationStatus {
  isSupported: boolean;
  permission: NotificationPermission | 'unsupported';
  isSubscribed: boolean;
}

class PushNotificationService {
  private vapidPublicKey: string | null = null;

  /**
   * Check if push notifications are supported in the current environment
   */
  isSupported(): boolean {
    if (Platform.OS !== 'web') return false;
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  /**
   * Get the current push notification status
   */
  async getStatus(): Promise<PushNotificationStatus> {
    if (!this.isSupported()) {
      return {
        isSupported: false,
        permission: 'unsupported',
        isSubscribed: false,
      };
    }

    const permission = Notification.permission;
    let isSubscribed = false;

    if (permission === 'granted') {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        isSubscribed = subscription !== null;
      } catch {
        // Ignore errors
      }
    }

    return {
      isSupported: true,
      permission,
      isSubscribed,
    };
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported');
    }

    return await Notification.requestPermission();
  }

  /**
   * Register the service worker
   */
  async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!this.isSupported()) {
      throw new Error('Service workers are not supported');
    }

    // Register the service worker
    const registration = await navigator.serviceWorker.register('/sw.js');

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;

    return registration;
  }

  /**
   * Get VAPID public key from the server
   */
  async getVapidPublicKey(): Promise<string> {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    const { publicKey } = await api.getVapidPublicKey();
    this.vapidPublicKey = publicKey;
    return publicKey;
  }

  /**
   * Subscribe to push notifications
   * This registers the subscription with the browser and then with our backend
   */
  async subscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported');
    }

    // Check permission
    const permission = Notification.permission;
    if (permission === 'denied') {
      throw new Error('Notification permission was denied');
    }

    // Request permission if needed
    if (permission !== 'granted') {
      const result = await this.requestPermission();
      if (result !== 'granted') {
        throw new Error('Notification permission was denied');
      }
    }

    try {
      // Register service worker
      await this.registerServiceWorker();

      // Get VAPID key
      const vapidKey = await this.getVapidPublicKey();

      // Get push subscription
      const registration = await navigator.serviceWorker.ready;

      // Check if already subscribed
      let subscription = await registration.pushManager.getSubscription();

      // If not subscribed, create new subscription
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
        });
      }

      // Register with backend
      const subscriptionJson = subscription.toJSON();
      if (subscriptionJson.endpoint && subscriptionJson.keys?.p256dh && subscriptionJson.keys?.auth) {
        await api.registerPushSubscription({
          endpoint: subscriptionJson.endpoint,
          p256dh: subscriptionJson.keys.p256dh,
          auth: subscriptionJson.keys.auth,
          userAgent: navigator.userAgent,
        });
      }

      return true;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unregister from backend
        try {
          await api.unregisterPushSubscription(subscription.endpoint);
        } catch {
          // Ignore backend errors - subscription might already be removed
        }

        // Unsubscribe from browser
        await subscription.unsubscribe();
      }

      return true;
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      throw error;
    }
  }

  /**
   * Check if user is currently subscribed to push notifications
   */
  async isSubscribed(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return subscription !== null;
    } catch {
      return false;
    }
  }
}

export const pushNotifications = new PushNotificationService();
